import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ArrowUpRight } from 'lucide-react';
import './index.css';
import { useLanguage } from './LanguageContext';
import { useTheme } from './ThemeContext';
import { Link, useLocation } from 'react-router-dom';
import ThreeScene from './ThreeScene';
import { translations } from './translations';
import Footer from './components/Footer';
import { LanguagePillSwitcher } from './components/LanguagePillSwitcher';
import PortfolioSection from './components/PortfolioSection';
import { HeroRotatingSlogan, getHeroCopyFrameStyle } from './components/HeroRotatingSlogan';
import { getSpiritsSectionTopGapVh } from './portfolioGridData';
import { SkillGlassTitle } from './components/SkillGlassTitle';
import { useSkillGlassTitleSize } from './hooks/useSkillGlassTitleSize';
import { usePrefersReducedMotion } from './hooks/usePrefersReducedMotion';

// Import assets
// import video from './assets/adobe_2.mp4';
import img1 from './assets/1.jpeg';
import img2 from './assets/2.jpeg';
import imgAdobe from './assets/AdobeStock_1155900392.jpeg';
import imgRsqProject from './assets/rsq_max.png';
import imgPhoneRsq from './assets/phone_rsq.png';
import imgLmbProject from './assets/LMB_max.png';
import imgPhoneLmb from './assets/phone_LMB.png';
import imgProdukt from './assets/produkt.jpeg';
import imgVocaProject from './assets/voca_max.png';
import imgVocaMobile from './assets/voca_phone.png';
import imgRemaxProject from './assets/remax_mac.png';
import imgPhoneRemax from './assets/phone_rmx.png';
import imgAmm from './assets/amm.svg';
import imgAmmWhite from './assets/amm-weiß.svg';
import portraitImg from './assets/portrait.png';

/** Desktop: preload + fly-in fire before the section enters the viewport */
const DESKTOP_SKILLS_PRELOAD_ROOT_MARGIN = '0% 0% 60% 0%';
const DESKTOP_SKILLS_FLY_IN_ROOT_MARGIN = '0% 0% 35% 0%';

const DUAL_MOCKUP_PROJECT_TITLES = new Set([
  'RSQDATA',
  'Remax Carsearch Redesign',
  'VOCA - Barrierefreies Kommunikationssystem',
  'VOCA - Barrier-Free Communication System',
  'LMB Marketing',
]);

function isDualMockupProject(title) {
  return DUAL_MOCKUP_PROJECT_TITLES.has(title);
}

function getProjectPhoneImage(title) {
  if (title === 'RSQDATA') {
    return imgPhoneRsq;
  }

  if (title === 'Remax Carsearch Redesign') {
    return imgPhoneRemax;
  }

  if (title === 'LMB Marketing') {
    return imgPhoneLmb;
  }

  return imgVocaMobile;
}

function preloadSkillImages(images) {
  images.forEach((src) => {
    const img = new Image();
    img.decoding = 'async';
    img.src = src;
  });
}

function getSkillCardEnterClass(index, visible, flyInDone, reducedMotion, isMobile) {
  if (reducedMotion) {
    return isMobile
      ? 'opacity-100 translate-x-0 translate-y-0'
      : 'opacity-100 translate-x-0 translate-y-0 skill-card-fly-in--motion-done';
  }

  if (!isMobile) {
    const side = index < 2 ? 'skill-card-fly-in--from-left' : 'skill-card-fly-in--from-right';
    const motionDone = flyInDone ? ' skill-card-fly-in--motion-done' : '';
    return `skill-card-fly-in ${side}${visible ? ' skill-card-fly-in--active' : ''}${motionDone}`;
  }

  if (visible) {
    return 'opacity-100 translate-x-0 translate-y-0';
  }

  return 'opacity-0 translate-y-4';
}

const Portfolio = () => {
  // const videoRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  // const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  // const [isHovering, setIsHovering] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [thinkBigX, setThinkBigX] = useState(-400);
  const [handleDigitalX, setHandleDigitalX] = useState(400);
  const [headlineX, setHeadlineX] = useState(400);
  const [skillVisible, setSkillVisible] = useState([false, false, false, false]);
  const [skillFlyInDone, setSkillFlyInDone] = useState([false, false, false, false]);
  const [skillTitleEnter, setSkillTitleEnter] = useState([false, false, false, false]);
  const [skillActive, setSkillActive] = useState([false, false, false, false]);
  const [isMobile, setIsMobile] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window !== 'undefined' ? window.innerHeight : 800
  );
  const [introTextOpacity, setIntroTextOpacity] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [introTextTranslateY, setIntroTextTranslateY] = useState(30);
  const [wordAnimationProgress, setWordAnimationProgress] = useState(0);
  const skillRefs = [useRef(), useRef(), useRef(), useRef()];
  const skillsSectionRef = useRef(null);
  const introTextRef = useRef(null);
  const location = useLocation();
  
  const { language } = useLanguage();
  const theme = useTheme();
  const reducedMotion = usePrefersReducedMotion();

  // Text in Wörter aufteilen für Animation
  const introText = language === 'de' 
    ? "Wir sind deine digitale Allround-Crew. Von schlanken Landing Pages bis hin zu leistungsstarken Webanwendungen – wir machen aus deiner Vision ein digitales Erlebnis, das begeistert. Mit unserem Know-how in Full-Stack-Entwicklung, modernem UI/UX-Design und strategischer Beratung schaffen wir Lösungen, die nicht nur gut aussehen, sondern auch Ergebnisse liefern."
    : "We are your digital all-round crew. From sleek landing pages to powerful web applications – we turn your vision into a captivating digital experience. With our expertise in full-stack development, modern UI/UX design, and strategic consulting, we create solutions that not only look good but also deliver results.";

  // Wörter mit Startpositionen für Animation (nur auf Mobile)
  const words = useMemo(() => {
    if (!isMobile) return null;
    
    return introText.split(' ').map((word, index) => {
      // Zufällige Richtung für jedes Wort
      const angle = (Math.random() * Math.PI * 2);
      const distance = 300 + Math.random() * 200; // 300-500px Entfernung
      const startX = Math.cos(angle) * distance;
      const startY = Math.sin(angle) * distance;
      
      return {
        word: word,
        index,
        startX,
        startY,
        delay: index * 0.03, // Stagger-Effekt: jedes Wort startet etwas später
      };
    });
  }, [introText, isMobile]);

  // Image mapping arrays (replaces inline ternary chains)
  const skillImages = [img1, imgAdobe, img2, imgProdukt];
  // const skillCategories = ['web', 'graphic', 'uiux', 'photo'];
  // const projectImages = [imgRemax, imgAmm, imgInfinity, imgComing];

  // Get translations with theme-aware icons
  const content = translations(theme.colors.icon);
  const currentContent = content[language];
  const skillTitles = useMemo(
    () => currentContent.about.skills.map((skill) => skill.title),
    [currentContent.about.skills]
  );
  const { containerRef: skillGlassContainerRef, fontSize: skillGlassFontSize } =
    useSkillGlassTitleSize(skillTitles, isMobile);
  
  // Destructure theme colors for easier access
  const { bg: bgColor, text: textColor, borderOpacity, textOpacity } = theme.colors;
  const heroCopyFrameStyle = getHeroCopyFrameStyle(isMobile);

  useEffect(() => {
    setIsVisible(true);
    
    // Check if iPhone/mobile on initial load
    const checkMobile = () => {
      const isIPhone = /iPhone|iPad|iPod/.test(navigator.userAgent);
      const isMobileWidth = window.innerWidth < 768;
      setIsMobile(isIPhone || isMobileWidth);
      setViewportHeight(window.innerHeight);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    if (location.state?.scrollToContact) {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  // Add effect for video speed
  // useEffect(() => {
  //   if (videoRef.current) {
  //     videoRef.current.playbackRate = 0.1;
  //   }
  // }, []);

  useEffect(() => {
    const onScroll = () => {
      const scrollY = window.scrollY;
      const start = 0;
      const end = isMobile ? Math.round(viewportHeight * 0.22) : Math.round(viewportHeight * 0.25);
      const maxOffset = isMobile ? Math.round(viewportHeight * 0.22) : Math.round(viewportHeight * 0.28);
      
      if (isMobile) {
        if (scrollY < start) {
          setThinkBigX(-maxOffset);
          setHandleDigitalX(maxOffset);
        } else if (scrollY > end) {
          setThinkBigX(0);
          setHandleDigitalX(0);
        } else {
          const progress = (scrollY - start) / (end - start);
          setThinkBigX(-maxOffset + maxOffset * progress);
          setHandleDigitalX(maxOffset - maxOffset * progress);
        }
        return;
      }

      if (scrollY < start) {
        setHeadlineX(maxOffset);
      } else if (scrollY > end) {
        setHeadlineX(0);
      } else {
        const progress = (scrollY - start) / (end - start);
        setHeadlineX(maxOffset - maxOffset * progress);
      }
    };

    // Scroll listener für Desktop UND Mobile
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // Initial check
    
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [isMobile, viewportHeight]);

  useEffect(() => {
    if (isMobile) {
      const observers = skillRefs.map((ref, i) => {
        return new window.IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              setSkillVisible((prev) => {
                const updated = [...prev];
                updated[i] = true;
                return updated;
              });
            }
          },
          { threshold: 0.1 }
        );
      });

      skillRefs.forEach((ref, i) => {
        if (ref.current) observers[i].observe(ref.current);
      });

      return () => {
        observers.forEach((observer, i) => {
          if (skillRefs[i].current) observer.unobserve(skillRefs[i].current);
        });
      };
    }

    const section = skillsSectionRef.current;
    if (!section) {
      return undefined;
    }

    let triggered = false;
    let preloaded = false;

    const triggerFlyIn = () => {
      if (triggered) {
        return;
      }

      triggered = true;
      setSkillVisible([true, true, true, true]);
    };

    const preloadObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || preloaded) {
          return;
        }

        preloaded = true;
        preloadSkillImages(skillImages);
        preloadObserver.disconnect();
      },
      { threshold: 0, rootMargin: DESKTOP_SKILLS_PRELOAD_ROOT_MARGIN }
    );

    const flyInObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          return;
        }

        triggerFlyIn();
        flyInObserver.disconnect();
      },
      { threshold: 0, rootMargin: DESKTOP_SKILLS_FLY_IN_ROOT_MARGIN }
    );

    preloadObserver.observe(section);
    flyInObserver.observe(section);

    return () => {
      preloadObserver.disconnect();
      flyInObserver.disconnect();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  useEffect(() => {
    const observers = skillRefs.map((ref, i) => {
      return new window.IntersectionObserver(
        ([entry]) => {
          if (entry.intersectionRatio >= 0.5) {
            setSkillTitleEnter((prev) => {
              if (prev[i]) {
                return prev;
              }
              const updated = [...prev];
              updated[i] = true;
              return updated;
            });
          }
        },
        { threshold: [0, 0.25, 0.5, 0.75], rootMargin: '0% 0% -8% 0%' }
      );
    });

    skillRefs.forEach((ref, i) => {
      if (ref.current) {
        observers[i].observe(ref.current);
      }
    });

    return () => {
      observers.forEach((observer, i) => {
        if (skillRefs[i].current) {
          observer.unobserve(skillRefs[i].current);
        }
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll-Listener für Kacheln in der Mitte des Bildschirms (nur auf Mobile)
  useEffect(() => {
    if (!isMobile) {
      // Auf Desktop: alle Skills auf inaktiv setzen (nur Hover funktioniert)
      setSkillActive([false, false, false, false]);
      return;
    }

    const checkCenterPosition = () => {
      skillRefs.forEach((ref, i) => {
        if (ref.current) {
          const rect = ref.current.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const centerStart = viewportHeight * 0.4;
          const centerEnd = viewportHeight * 0.6;
          const elementCenter = rect.top + rect.height / 2;
          
          const isInCenter = elementCenter >= centerStart && elementCenter <= centerEnd && 
                            rect.top < viewportHeight && rect.bottom > 0;
          
          setSkillActive(prev => {
            const updated = [...prev];
            updated[i] = isInCenter;
            return updated;
          });
        }
      });
    };

    // Initial check
    checkCenterPosition();
    
    // Check on scroll (nur auf Mobile)
    window.addEventListener('scroll', checkCenterPosition, { passive: true });
    window.addEventListener('resize', checkCenterPosition);

    return () => {
      window.removeEventListener('scroll', checkCenterPosition);
      window.removeEventListener('resize', checkCenterPosition);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  // Scroll-basierte Animation für Intro-Text (nur auf Mobile)
  useEffect(() => {
    if (!isMobile) {
      // Auf Desktop: Text immer sichtbar
      setIntroTextOpacity(1);
      setIntroTextTranslateY(0);
      return;
    }

    const onScroll = () => {
      const scrollY = window.scrollY;
      const animationStart = Math.round(viewportHeight * 1.08);
      const animationEnd = Math.round(viewportHeight * 1.48);
      
      if (scrollY < animationStart) {
        setIntroTextOpacity(0);
        setIntroTextTranslateY(30);
        setWordAnimationProgress(0);
      } else if (scrollY > animationEnd) {
        setIntroTextOpacity(1);
        setIntroTextTranslateY(0);
        setWordAnimationProgress(1);
      } else {
        const progress = (scrollY - animationStart) / (animationEnd - animationStart);
        setIntroTextOpacity(progress);
        setIntroTextTranslateY(30 * (1 - progress));
        setWordAnimationProgress(progress);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // Initial check
    
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [isMobile, viewportHeight]);

  // const handleMouseMove = (e) => {
  //   const rect = e.currentTarget.getBoundingClientRect();
  //   setMousePosition({
  //     x: e.clientX - rect.left,
  //     y: e.clientY - rect.top
  //   });
  //   setIsHovering(true);
  // };

  // const handleMouseLeave = () => {
  //   setIsHovering(false);
  // };

  // Button-Klassen aus ThemeContext
  const buttonClass = theme.button.base;
  // const buttonClassMobile = theme.button.mobile;

  return (
    <div className={`min-h-screen w-full ${bgColor} ${textColor} font-mono`} style={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
      {/* Fixed Header Bar - Durchgehend mit Fade nach unten */}
      <div 
        className={`fixed top-0 left-0 right-0 w-full z-50 backdrop-blur-sm shadow-lg`}
        style={{
          background: theme.isDarkMode 
            ? 'linear-gradient(to bottom, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.7) 50%, rgba(0, 0, 0, 0) 100%)'
            : 'linear-gradient(to bottom, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 50%, rgba(255, 255, 255, 0) 100%)'
        }}
      >
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex justify-between items-center h-14 md:h-16">
            {/* Desktop Navigation - Links */}
            <div className="hidden md:flex space-x-2 items-center">
              <Link 
                to="/services" 
                className={buttonClass}
              >
                <span>{currentContent.nav.services}</span>
                <ArrowUpRight size={12} className="flex-shrink-0" />
              </Link>
              <button 
                onClick={() => {
                  const element = document.getElementById('projects');
                  const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                  const offsetPosition = elementPosition - window.innerHeight * 0.1;
                  window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                }}
                className={buttonClass}
              >
                <span>{currentContent.nav.projects}</span>
                <ArrowUpRight size={12} className="flex-shrink-0" />
              </button>
              <button 
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                className={buttonClass}
              >
                <span>{currentContent.nav.contact}</span>
                <ArrowUpRight size={12} className="flex-shrink-0" />
              </button>
            </div>

            {/* Mobile Menu Button & Language Switch - Links */}
            <div className="flex items-center space-x-1.5 md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`${buttonClass} min-h-[40px]`}
              >
                <span>{language === 'de' ? 'Navigation' : 'Navigation'}</span>
                <ArrowUpRight size={12} className="flex-shrink-0" />
              </button>
              <LanguagePillSwitcher />
            </div>

            {/* Language Switch & Login - Rechts (Desktop only) */}
            <div className="hidden md:flex items-center space-x-1.5">
              <Link
                to="/login"
                className={buttonClass}
              >
                <span>Login</span>
                <ArrowUpRight size={12} className="flex-shrink-0" />
              </Link>
              <LanguagePillSwitcher />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {isMenuOpen && (
        <div 
          className={`fixed top-14 md:top-16 left-0 right-0 w-full h-auto z-50 md:hidden border-b ${borderOpacity} backdrop-blur-sm`}
          style={{
            background: theme.isDarkMode 
              ? 'linear-gradient(to bottom, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.5) 50%, rgba(0, 0, 0, 0) 100%)'
              : 'linear-gradient(to bottom, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0.5) 50%, rgba(255, 255, 255, 0) 100%)'
          }}
        >
          <div className="px-4 py-4 flex flex-col space-y-2 items-start">
            <Link 
              to="/services" 
              className={`self-start justify-start flex items-center gap-2 px-[13.2px] py-[4.4px] rounded-[22px] border-none transition-all font-mono text-sm min-h-[40px] bg-blue-500/50 border-l-4 border-l-blue-400 ${theme.isDarkMode ? 'text-white' : 'text-black'}`}
              onClick={() => setIsMenuOpen(false)}
            >
              <span>{currentContent.nav.services}</span>
              <ArrowUpRight size={11} className="flex-shrink-0" />
            </Link>
            <button 
              onClick={() => {
                setIsMenuOpen(false);
                const element = document.getElementById('projects');
                const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                const offsetPosition = elementPosition - window.innerHeight * 0.1;
                window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
              }}
              className={`self-start justify-start flex items-center gap-2 px-[13.2px] py-[4.4px] rounded-[22px] border-none transition-all font-mono text-sm min-h-[40px] bg-purple-500/50 border-l-4 border-l-purple-400 ${theme.isDarkMode ? 'text-white' : 'text-black'}`}
            >
              <span>{currentContent.nav.projects}</span>
              <ArrowUpRight size={11} className="flex-shrink-0" />
            </button>
            <button 
              onClick={() => {
                setIsMenuOpen(false);
                document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
              }}
              className={`self-start justify-start flex items-center gap-2 px-[13.2px] py-[4.4px] rounded-[22px] border-none transition-all font-mono text-sm min-h-[40px] bg-green-500/50 border-l-4 border-l-green-400 ${theme.isDarkMode ? 'text-white' : 'text-black'}`}
            >
              <span>{currentContent.nav.contact}</span>
              <ArrowUpRight size={11} className="flex-shrink-0" />
            </button>
            <Link
              to="/login"
              className={`self-start justify-start flex items-center gap-2 px-[13.2px] py-[4.4px] rounded-[22px] border-none transition-all font-mono text-sm min-h-[40px] bg-orange-500/50 border-l-4 border-l-orange-400 ${theme.isDarkMode ? 'text-white' : 'text-black'}`}
              onClick={() => setIsMenuOpen(false)}
            >
              <span>Login</span>
              <ArrowUpRight size={14} className="flex-shrink-0" />
            </Link>
          </div>
        </div>
      )}
              
      {/* Hero Section with Video */}
      <div className={`transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} font-mono pt-16 md:pt-20`}>
        <div className="relative">
          {/* BCRE8IVE Title */}
          <div className="fixed left-0 right-0 z-20 text-center" style={{ top: 'calc(50vh - 10vh)' }}>
            <h1 
              className={`text-[75px] md:text-[210px] font-mono ${textColor} tracking-tight leading-none mx-auto max-w-none`} 
              style={{ 
                background: 'transparent',
                marginBottom: isMobile ? '40px' : '0px'
              }}
            >
              BCRE8IVE
            </h1>
              </div>

          {/* 3D Scene Section - Extended coverage below text */}
          <div
            className={`fixed left-0 right-0 w-full z-10 ${isMobile ? 'h-[150vh]' : 'h-[90vh]'}`}
            style={{ top: '10vh' }}
          >
            <div className="absolute inset-0 h-full w-full overflow-hidden">
              <ThreeScene />
            </div>
          </div>
          
          {/* Slogan + intro appear only after the hero scroll track */}
          <div aria-hidden="true" className="h-[100svh] shrink-0" />

          <HeroRotatingSlogan
            slogans={currentContent.hero.slogans}
            isMobile={isMobile}
            textColor={textColor}
            line1OffsetX={isMobile ? thinkBigX : headlineX}
            line2OffsetX={isMobile ? handleDigitalX : headlineX}
            reducedMotion={reducedMotion}
          />

          {/* Introduction Text Block */}
          {isMobile ? (
            <div className="relative z-30 w-full bg-transparent" style={heroCopyFrameStyle}>
              <p
                ref={introTextRef}
                className={`text-2xl font-mono leading-relaxed ${textColor} text-left font-medium pb-12`}
                  style={{
                    opacity: introTextOpacity,
                    display: words ? 'flex' : 'block',
                    flexWrap: words ? 'wrap' : 'nowrap',
                    justifyContent: words ? 'flex-start' : 'normal',
                  }}
                >
                  {words ? (
                    words.map((wordObj, index) => {
                      const wordProgress = Math.max(0, Math.min(1, 
                        (wordAnimationProgress - wordObj.delay) / (1 - wordObj.delay)
                      ));
                      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
                      const easedProgress = easeOutCubic(wordProgress);
                      const currentX = wordObj.startX * (1 - easedProgress);
                      const currentY = wordObj.startY * (1 - easedProgress) + (30 * (1 - wordAnimationProgress));
                      const opacity = easedProgress;
                      
                      return (
                        <span
                          key={index}
                          style={{
                            display: 'inline-block',
                            transform: `translate(${currentX}px, ${currentY}px)`,
                            opacity: opacity,
                            transition: 'opacity 0.1s ease-out',
                            marginRight: '0.25em',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {wordObj.word}
                        </span>
                      );
                    })
                  ) : (
                    introText
                  )}
                </p>
            </div>
          ) : (
            <div className="relative z-30 w-full pb-[6svh]" style={heroCopyFrameStyle}>
              <p
                ref={introTextRef}
                className={`text-2xl font-mono leading-relaxed ${textColor} text-right font-medium`}
              >
                {introText}
              </p>
            </div>
          )}

          <div aria-hidden="true" className="h-[14svh] shrink-0 md:h-[18svh]" />
        </div>
      </div>

      <PortfolioSection />

      {/* Our Spirits Section - Two Column Layout */}
      <div
        data-spirits-section
        className="relative isolate w-screen bg-white font-mono"
        style={{ marginLeft: 'calc(50% - 50vw)' }}
      >
        <div
          className="container mx-auto px-4 pb-24 md:pb-32"
          style={{ paddingTop: `${getSpiritsSectionTopGapVh(isMobile)}vh` }}
        >
        <div className="w-full md:w-[90%] md:mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center min-h-0 md:min-h-[650px] relative">
            {/* Left Column - Image */}
            <div className="relative h-[400px] md:h-[600px] flex items-end justify-start order-2 md:order-1">
              <div className="relative w-full h-full">
                {/* Title above image */}
                <div className="absolute top-0 left-0 z-10">
                  <h3 className="text-4xl md:text-5xl font-mono text-black mb-3">
                    {language === 'de' ? 'Unsere Werte' : 'Our Spirits'}
                  </h3>
                </div>
                
                {/* Portrait Image - Black & White, positioned in bottom-left corner */}
                <img 
                  src={portraitImg}
                  alt="Portrait"
                  className="absolute bottom-0 left-0 w-[75%] md:w-[85%] h-auto object-contain filter grayscale contrast-110 brightness-95"
                  style={{
                    maxHeight: '85%',
                    objectPosition: 'left bottom',
                    zIndex: 1
                  }}
                />
              </div>
            </div>

            {/* Right Column - Text (Desktop) */}
            <div className="hidden md:flex flex-col justify-center px-4 md:px-8 order-1 md:order-2 text-black">
              <blockquote className="text-2xl md:text-4xl font-mono leading-relaxed italic">
                {language === 'de' 
                  ? "„Wir sind kein Konzern-Deck, sondern Leute, die gestalten und entwickeln. Was wir bauen, soll morgen noch Sinn ergeben — nicht nur heute gut aussehen.“"
                  : "\"We're not a corporate deck — we're people who design and build. What we make should still make sense tomorrow, not just look good today.\""
                }
              </blockquote>
            </div>

            {/* Mobile: Quote below image */}
            <div className="md:hidden order-3 px-4 text-black">
              <blockquote className="text-xl font-mono leading-relaxed italic">
                {language === 'de' 
                  ? "„Wir sind kein Konzern-Deck, sondern Leute, die gestalten und entwickeln. Was wir bauen, soll morgen noch Sinn ergeben — nicht nur heute gut aussehen.“"
                  : "\"We're not a corporate deck — we're people who design and build. What we make should still make sense tomorrow, not just look good today.\""
                }
              </blockquote>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Skills Section - Kachel Design */}
      <div id="about" ref={skillsSectionRef} className="relative container mx-auto px-4 pt-24 pb-12 md:pt-32 md:pb-16 font-mono md:overflow-visible">
        <div className="w-full md:w-[90%] md:mx-auto" style={isMobile ? {} : { paddingTop: '80px', paddingBottom: '80px' }}>
          <h2 className={`text-5xl font-mono mb-12 ${textColor}`}>
            Skills
          </h2>
          <div ref={skillGlassContainerRef} className="flex flex-col md:flex-row md:flex-nowrap gap-4 md:gap-6 md:overflow-visible">
            {currentContent.about.skills.map((skill, index) => {
              const skillTags = skill.skills.split(' • ');
              
              return (
                <div
                  ref={skillRefs[index]}
                  key={skill.title}
                  className={`
                    group relative shadow-lg
                    rounded-[28px] md:rounded-2xl
                    ${isMobile ? 'transition-all duration-500 ease-out' : ''}
                    ${getSkillCardEnterClass(index, skillVisible[index], skillFlyInDone[index], reducedMotion, isMobile)}
                    ${theme.isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}
                    ${textColor}
                    hover:shadow-xl cursor-pointer
                    w-full md:flex-1 md:min-w-0
                    md:hover:flex-[1.5]
                  `}
                  style={
                    reducedMotion
                      ? undefined
                      : isMobile
                        ? { transitionDelay: `${index * 100}ms` }
                        : { animationDelay: `${index * 160}ms` }
                  }
                  onAnimationEnd={() => {
                    if (!isMobile) {
                      setSkillFlyInDone((prev) => {
                        if (prev[index]) {
                          return prev;
                        }

                        const updated = [...prev];
                        updated[index] = true;
                        return updated;
                      });
                    }
                  }}
                  onTouchStart={() => {
                    setSkillActive(prev => {
                      const updated = [...prev];
                      updated[index] = !updated[index];
                      return updated;
                    });
                  }}
                >
                  <div className="absolute inset-0 transition-opacity duration-500 pointer-events-none overflow-hidden rounded-[28px] md:rounded-2xl">
                    <img 
                      src={skillImages[index]}
                      alt={skill.title}
                      className="w-full h-full object-cover"
                      loading={isMobile ? 'lazy' : 'eager'}
                      decoding="async"
                    />
                    <div className={`absolute inset-0 transition-opacity duration-500 ${skillActive[index] ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} ${theme.isDarkMode ? 'bg-black/60' : 'bg-white/60'}`}></div>
                  </div>

                  {/* Mobile: frosted glass title at bottom, 5% cropped */}
                  <div className="md:hidden absolute inset-x-0 bottom-0 z-10 pointer-events-none overflow-hidden rounded-b-[28px]">
                    <div
                      className="absolute inset-x-0 bottom-0 h-[42%] backdrop-blur-[3px] bg-gradient-to-t from-white/12 via-white/5 to-transparent"
                      aria-hidden="true"
                    />
                    <div className="relative overflow-hidden px-4 pb-0">
                      <SkillGlassTitle
                        title={skill.title}
                        index={index}
                        fontSizePx={skillGlassFontSize}
                        isActive={skillActive[index]}
                        darkWhenIdle={index === 0 || index === 2}
                        isVisible={skillTitleEnter[index]}
                        fromLeft={index % 2 === 0}
                        enterDelayMs={index * 80}
                      />
                    </div>
                  </div>
                  
                  <div className="relative z-10 h-full flex flex-col p-4 md:p-8 min-h-[240px] md:min-h-[450px]">
                    {skillTags.length > 0 && (
                      <div className={`flex flex-wrap gap-2 mb-4 transition-opacity duration-300 ${skillActive[index] ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                        {skillTags.map((tag, tagIndex) => (
                          <span 
                            key={tagIndex}
                            className="px-3 py-1 rounded-full text-xs font-mono border backdrop-blur-xl md:bg-white md:text-black md:border-black/20 bg-white/15 text-white border-white/25"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    <div className="hidden md:block mt-auto text-right">
                      <h3 className={`text-xl font-mono font-semibold transition-colors duration-300 ${
                        (skill.title === 'Grafikdesign' || skill.title === 'Graphic Design' || 
                         skill.title === 'Fotografie' || skill.title === 'Photography') 
                          ? 'text-white' 
                          : theme.isDarkMode 
                            ? skillActive[index] ? 'text-white' : 'text-black group-hover:text-white'
                            : 'text-black group-hover:text-black'
                      }`}>
                        {skill.title}
                      </h3>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Projects Section - Layout wie im Bild */}
      <div id="projects" className="relative container mx-auto px-4 py-12 md:py-16 font-mono">
        <div className="w-full md:w-[90%] md:mx-auto">
          <h2 className={`text-3xl md:text-4xl lg:text-5xl font-mono mb-12 md:mb-16 ${textColor}`}>
            {currentContent.projects.title}
          </h2>
          <div className="space-y-32 md:space-y-48">
            {currentContent.projects.items.map((project, index) => {
              const projectImages = [imgRsqProject, imgLmbProject, imgRemaxProject, imgVocaProject, imgAmm];
              let projectImage = projectImages[index] || "";
              if (projectImage === imgAmm && theme.isDarkMode) {
                projectImage = imgAmmWhite;
              }

              const textOnRight = index === 1;

              return (
                <div
                  key={`${project.title}-${index}`}
                  className={`flex flex-col ${textOnRight ? 'md:flex-row-reverse' : 'md:flex-row'} items-start gap-6 md:gap-8 lg:gap-12`}
                >
                  {/* Text Block */}
                  <div className={`space-y-6 ${isDualMockupProject(project.title) ? 'flex-[0.8]' : 'flex-1'} ${textOnRight ? 'text-right' : ''}`}>
                    <h3 className={`font-mono font-semibold text-2xl md:text-3xl lg:text-4xl ${textColor}`}>
                      {project.title}
                    </h3>
                    <p className={`text-base font-mono ${textOpacity} leading-relaxed`}>
                      {project.description}
                    </p>
                    
                    {/* Tech Tags */}
                    <div className={`flex flex-wrap gap-3 ${textOnRight ? 'justify-end' : ''}`}>
                      {project.tech.map((tech, techIndex) => (
                        <span 
                          key={techIndex} 
                          className={`
                            px-4 py-2 rounded-full text-sm font-mono border
                            ${theme.isDarkMode 
                              ? 'bg-white/10 text-white/80 border-white/20' 
                              : 'bg-black/10 text-black/80 border-black/20'
                            }
                          `}
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                    
                    {/* Link Button */}
                    {project.link && (
                      <a 
                        href={project.link}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={`
                          inline-block px-6 py-3 rounded-lg text-base font-mono border-2 transition-all
                          ${theme.isDarkMode 
                            ? 'border-white text-white hover:bg-white hover:text-black' 
                            : 'border-black text-black hover:bg-black hover:text-white'
                          }
                        `}
                      >
                        {language === 'de' ? 'Zur Website →' : 'Visit Website →'}
                      </a>
                    )}
                  </div>
                  
                  {/* Image Block */}
                  {projectImage && (
                    <div className={`w-full ${isDualMockupProject(project.title) ? 'flex-[1.2]' : 'flex-1'}`}>
                      {isDualMockupProject(project.title) ? (
                        <div className="flex items-stretch gap-3 md:gap-4">
                          <div className="relative flex flex-[1.5] items-center justify-center bg-transparent">
                            <img
                              src={projectImage}
                              alt={project.title}
                              className="h-auto w-full object-contain"
                            />
                          </div>
                          <div className="relative flex min-w-[32%] flex-1 items-center justify-center md:min-w-[34%]">
                            <img
                              src={getProjectPhoneImage(project.title)}
                              alt={`${project.title} Mobile`}
                              className="h-full max-h-full w-auto max-w-full object-contain"
                              style={{
                                minHeight: isMobile ? '14.4rem' : '19.8rem',
                                maxHeight: isMobile ? '18rem' : '90%',
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className={`relative ${project.title === "Logistik Intranet" || project.title === "Logistics Intranet" ? 'flex items-center justify-start' : 'overflow-hidden rounded-xl shadow-lg'} w-full md:w-[80%] md:mx-auto`}>
                          {project.title === "Logistik Intranet" || project.title === "Logistics Intranet" ? (
                            <img
                              src={projectImage}
                              alt={project.title}
                              className="object-contain"
                              style={{
                                width: isMobile ? '68%' : '80%',
                                height: 'auto',
                                maxHeight: isMobile ? '68%' : '80%',
                              }}
                            />
                          ) : (
                            <img
                              src={projectImage}
                              alt={project.title}
                              className="w-full h-full object-cover aspect-[4/3]"
                            />
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {currentContent.projects.teasers?.length > 0 && (
            <div className="mt-32 md:mt-48 flex flex-col items-center text-center space-y-16 md:space-y-20">
              <h2
                className={`font-mono font-black text-6xl md:text-8xl lg:text-9xl ${textColor} tracking-tight`}
                style={{ fontStyle: 'oblique' }}
              >
                {language === 'de' ? 'DEMNÄCHST' : 'COMING SOON'}
              </h2>
              <div className="w-full max-w-3xl space-y-10 md:space-y-12">
                {currentContent.projects.teasers.map((teaser) => (
                  <div key={teaser.name} className="space-y-2">
                    <h3 className={`font-mono text-2xl md:text-3xl font-semibold ${textColor}`}>
                      {teaser.name}
                    </h3>
                    <p className={`font-mono text-base leading-relaxed ${textOpacity}`}>
                      {teaser.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Contact Section - Using Footer Component */}
      <Footer />
    </div>
  );
};

export default Portfolio;